import { useRef, useState } from "react";
import { toast } from "react-toastify";
import * as patientApi from "../api/patientApi";
import { registrarBusquedaPaciente } from "../api/patientApi";
import useAuth from "./useAuth";

const usePatientData = () => {
  const { sedeActiva } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [unicId, setUnicId] = useState("");
  const [tipoUnicId, setTipoUnicId] = useState("");
  const [tiposIdentificacion, setTiposIdentificacion] = useState([]);
  const [patientData, setPatientData] = useState(null);
  const [yearOldData, setYearOldData] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [GruposData, setGruposData] = useState(null);
  const [preguntasData, setPreguntasData] = useState(null);
  const [pacientesModal, setPacientesModal] = useState([]);
  const [loadingPacientesModal, setLoadingPacientesModal] = useState(false);
  const pacientesAbortRef = useRef(null);
  const pacientesReqIdRef = useRef(0);
  const searchOriginRef = useRef(null);
  const [showEmbarazoModal, setShowEmbarazoModal] = useState(false);
  const [fechaGestacion, setFechaGestacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Procesando paciente...");

  const ensureActiveContext = () => {
    if (sedeActiva?.id && sedeActiva?.id_empresa) {
      return true;
    }

    toast.warning("Debe seleccionar una sede activa antes de consultar pacientes");
    return false;
  };


  const fetchTipoId = async () => {
    try {
      const rs = await patientApi.getTiposIdentificacion();
      setTiposIdentificacion(rs.data || []);
    } catch (error) {
      toast.error("Error al obtener tipos de identificación!");
      console.error(error);
    }
  };

  const buildNombreCompleto = (p) => {
    const partes = [
      p?.Primer_Apellido || p?.primer_apellido || "",
      p?.Segundo_Apellido || p?.segundo_apellido || "",
      p?.Primer_Nombre || p?.primer_nombre || "",
      p?.Segundo_Nombre || p?.segundo_nombre || "",
      p?.fullname || p?.NombreCompleto || ""
    ].filter(Boolean);
    if (partes.length === 0) return null;
    return partes.join(" ").trim().slice(0, 300) || null;
  };

  const registrarLogBusquedaPaciente = (datosPaciente, origen) => {
    if (!datosPaciente) return;
    const origenValido = ["search_input", "search_modal", "external_import"].includes(origen)
      ? origen
      : "search_input";

    const tipo = datosPaciente.Tipo_id || datosPaciente.tipo_id || "";
    const id = datosPaciente.Identificacion || datosPaciente.identificacion || datosPaciente.id || "";
    const unicId = datosPaciente.Unic_ID || datosPaciente.unic_id || (tipo && id ? `${tipo}${id}` : null);
    const autoid = datosPaciente.autoid || datosPaciente.Autoid || null;

    if (!unicId) return;

    const payload = {
      unic_id: unicId,
      autoid: autoid,
      origen_busqueda: origenValido,
    };

    registrarBusquedaPaciente(payload).catch((err) => {
      console.warn("[LogBusquedaPaciente] No se pudo registrar el log:", err?.message || err);
    });
  };

  const setSearchOrigin = (origin) => {
    searchOriginRef.current = origin;
  };

  const fetchPatientData = async (originParam = null) => {
    if (!unicId) {
      toast.warning("Debe ingresar tipo e identificación del paciente");
      searchOriginRef.current = null;
      return;
    }

    if (!ensureActiveContext()) {
      searchOriginRef.current = null;
      return;
    }

    try {
      const resData = await patientApi.getPaciente(unicId, tipoUnicId);

      if (!resData.success) {
        setPatientData(null);
        setResultData(null);
        setYearOldData(null);
        setGruposData(null);
        setPreguntasData(null);
        searchOriginRef.current = null;
        toast.error(resData.message);
        return;
      }

      const datosPaciente = resData.data.datos_paciente[0];

      setPatientData({
        ...datosPaciente,
        embarazada: resData.data.embarazada,
        fechaGestacion: resData.data.fechaGestacion,
        tieneSubgrupo9: resData.data.tieneSubgrupo9,
        tieneSubgrupo54: resData.data.tieneSubgrupo54,
        diagnosticos: resData.data.diagnosticos || datosPaciente?.diagnosticos || [],
      });

      setTipoUnicId(datosPaciente['Tipo_id']);

      setResultData(resData.data.resultado || null);
      setYearOldData(resData.data.edad || null);
      setGruposData(resData.data.grupos || null);
      setPreguntasData(resData.data.preguntas || null);
      setShowEmbarazoModal(resData.data.embarazada === 1);

      const origen = originParam || searchOriginRef.current;
      if (origen) {
        registrarLogBusquedaPaciente(datosPaciente, origen);
      }
      searchOriginRef.current = null;

    } catch (error) {
      const msg = error.response?.data?.message || "Error al obtener datos del backend!";
      toast.error(msg);
      console.error(error);
      setPatientData(null);
      setResultData(null);
      setYearOldData(null);
      setGruposData(null);
      setPreguntasData(null);
      searchOriginRef.current = null;
    }
  };

  const enviarRespuestaPregunta = async (idPregunta, resultado) => {
    if (!patientData?.Unic_ID || !patientData?.Tipo_id || !patientData?.Identificacion) {
      toast.error("Faltan datos del paciente para enviar la respuesta.");
      return;
    }

    try {
      await patientApi.saveAnswer({
        Unic_ID: patientData.Unic_ID,
        Tipo_id: patientData.Tipo_id,
        Identificacion: patientData.Identificacion,
        pregunta_id: idPregunta,
        resultado,
      });
      fetchPatientData();
    } catch (error) {
      console.error(error);
      toast.error("Error al enviar resultado");
    }
  };

  const getPacienteExterno = async () => {
    if (!unicId) {
      toast.warning("Debe ingresar número de identificación del paciente");
      return;
    }

    if (!ensureActiveContext()) {
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage("Importando paciente desde historia clínica...");
      const data = await patientApi.getPacienteExterno(unicId);

      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }

      if (data.success) {
        fetchPatientData("external_import");
      }

    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Error al obtener datos del backend!";
      toast.error(msg);
    } finally {
      setLoading(false);
      setLoadingMessage("Procesando paciente...");
    }
  };

  const exportPacienteExterno = async (forceUpdate = false) => {
    if (!patientData?.autoid || !patientData?.Tipo_id || !patientData?.Identificacion) {
      toast.warning("Debe consultar un paciente local válido antes de exportarlo.");
      return;
    }

    if (!ensureActiveContext()) {
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage(
        forceUpdate
          ? "Actualizando paciente en la base externa..."
          : "Exportando paciente a la base externa..."
      );

      const data = await patientApi.exportPacienteExterno({
        tipo_id: patientData.Tipo_id,
        identificacion: patientData.Identificacion,
        force_update: forceUpdate,
      });

      if (data?.data?.action === "exists_remote" && data?.data?.requiresConfirmation) {
        const confirmed = window.confirm(data.message);
        if (confirmed) {
          await exportPacienteExterno(true);
        }
        return;
      }

      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Error al exportar el paciente a la base externa.";
      toast.error(msg);
    } finally {
      setLoading(false);
      setLoadingMessage("Procesando paciente...");
    }
  };

  const fetchPacientesModal = async (filtro = "", filters = {}) => {
    if (!ensureActiveContext()) {
      setPacientesModal([]);
      return;
    }

    const reqId = ++pacientesReqIdRef.current;
    if (pacientesAbortRef.current) {
      pacientesAbortRef.current.abort();
    }
    const controller = new AbortController();
    pacientesAbortRef.current = controller;

    try {
      setLoadingPacientesModal(true);
      const res = await patientApi.getPacientesModal(filtro, filters, controller.signal);
      if (reqId !== pacientesReqIdRef.current) return;

      const transformados = (res.data.GetPacientes || []).map(p => ({
        id: p.Identificacion,
        tipo: p.Tipo_id,
        name: p.fullname,
        Unic_ID: p.Unic_ID,
        autoid: p.autoid,
        raw: p,
      }));

      setPacientesModal(transformados || []);
    } catch (error) {
      if (error?.code === "ERR_CANCELED") return;
      const msg = error.response?.data?.message || "Error al obtener pacientes del modal";
      toast.error(msg);
      console.error(error);
    } finally {
      if (reqId === pacientesReqIdRef.current) {
        setLoadingPacientesModal(false);
      }
    }
  };

  const handleGuardarFechaGestacion = async (datos) => {
    try {
      const data = await patientApi.saveFechaGestacion(datos);
      if (data.success) {
        fetchPatientData();
        setShowEmbarazoModal(false);
      } else {
        toast.error(data.mensaje || "Error al guardar la fecha de gestación");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar fecha de gestación");
    }
  };

  const handleOpenModal = (actividad, title) => {
    setSelectedActivity(actividad);
    setModalTitle(title);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedActivity(null);
    setModalTitle("");
  };
  const handleInputChange = (e) => setUnicId(e.target.value);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      searchOriginRef.current = "search_input";
      fetchPatientData();
    }
  };
  const handleClearAll = () => {
    setTipoUnicId(""); setUnicId(""); setPatientData(null); setResultData(null);
    setGruposData(null); setPreguntasData(null); setYearOldData(null);
    setShowEmbarazoModal(false); setFechaGestacion("");
    searchOriginRef.current = null;
  };

  const fetchPatientDataFromInput = () => {
    searchOriginRef.current = "search_input";
    fetchPatientData();
  };

  const canExportPaciente = Boolean(
    patientData?.autoid && patientData?.Tipo_id && patientData?.Identificacion
  );

  return {
    modalOpen, modalTitle, selectedActivity, unicId, tipoUnicId, tiposIdentificacion,
    patientData, yearOldData, resultData, GruposData, preguntasData,
    setUnicId, setTipoUnicId, setSearchOrigin,
    handleOpenModal, handleCloseModal, handleInputChange, handleKeyDown,
    fetchPatientData, fetchPatientDataFromInput, enviarRespuestaPregunta, handleClearAll,
    pacientesModal, loadingPacientesModal, fetchPacientesModal,
    getPacienteExterno, exportPacienteExterno, canExportPaciente,
    showEmbarazoModal, setShowEmbarazoModal,
    fechaGestacion, setFechaGestacion, handleGuardarFechaGestacion,
    loading, loadingMessage, fetchTipoId
  };
};

export default usePatientData;
